import { useState, useEffect, useMemo } from 'react';
import type { SessionSummary, TrackBest } from '@/types/telemetry';
import { formatTime } from '@/lib/format';
import { useRouter } from '@/context/RouterContext';
import { EmptyState } from '@/components/common/EmptyState';
import { useI18n } from '@/context/I18nContext';
import { WeatherBadge } from '@/components/races/WeatherBadge';
import { ChevronRight, Share2, CheckCircle2, Clock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

interface SessionListProps {
  sessions: SessionSummary[];
  trackBests?: TrackBest[];
  /** Reset key — when this changes, expanded state resets (e.g. on page/query change) */
  resetKey?: string;
}

interface SessionGroup {
  key: string;
  date: string;
  trackName: string;
  sessions: SessionSummary[];
}

function groupByEvent(sessions: SessionSummary[]): SessionGroup[] {
  const map = new Map<string, SessionGroup>();
  for (const s of sessions) {
    const key = `${s.date}::${s.trackName}`;
    let group = map.get(key);
    if (!group) {
      group = { key, date: s.date, trackName: s.trackName, sessions: [] };
      map.set(key, group);
    }
    group.sessions.push(s);
  }
  return Array.from(map.values());
}

/* Grid templates */
const EVENT_GRID = 'grid-cols-[24px_110px_1fr_90px_80px_80px]';
const SESSION_GRID = 'grid-cols-[24px_60px_1fr_90px_80px_80px]';

export function SessionList({ sessions, trackBests = [], resetKey }: SessionListProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();
  const { user } = useAuth();

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Reset expanded state on resetKey change
  useEffect(() => {
    setExpanded(new Set());
  }, [resetKey]);

  const eventGroups = useMemo(() => groupByEvent(sessions), [sessions]);

  // Build a map of track name → all-time best lap time for purple highlighting
  const trackBestMap = useMemo(() => {
    const m: Record<string, number> = {};
    for (const tb of trackBests) m[tb.trackName] = tb.bestLapTimeMs;
    return m;
  }, [trackBests]);

  const toggleEvent = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (sessions.length === 0) {
    return (
      <div className="card">
        <EmptyState icon="🏁" message={t('sessionList.noSessions')} />
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      {/* Desktop: event-level header */}
      <div className={`hidden sm:grid ${EVENT_GRID} gap-3 px-4 py-2.5 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted border-b border-border-subtle`}>
        <span></span>
        <span>{t('sessionList.date')}</span>
        <span>{t('sessionList.track')}</span>
        <span className="text-right">{t('sessionList.sessions')}</span>
        <span className="text-right">{t('sessionList.weather')}</span>
        <span className="text-right">{t('sessionList.best')}</span>
      </div>

      {eventGroups.map((group, gi) => {
        const isExpanded = expanded.has(group.key);
        const sessionCount = group.sessions.length;
        const groupBestMs = Math.min(...group.sessions.map(s => s.bestLapTimeMs).filter(t => t > 0));
        const isGroupTrackBest = isFinite(groupBestMs) && groupBestMs > 0 && trackBestMap[group.trackName] === groupBestMs;
        const isGroupProcessed = group.sessions.length > 0 && group.sessions.every(s => s.analysisStatus === 'completed');

        // Find weather from the fastest session of the day
        const fastestSession = isFinite(groupBestMs) ? group.sessions.find(s => s.bestLapTimeMs === groupBestMs) : undefined;
        const groupWeather = fastestSession?.weather;

        return (
          <div key={group.key}>
            {/* Event parent row (desktop) */}
            <div
              onClick={() => toggleEvent(group.key)}
              className={`hidden sm:grid ${EVENT_GRID} gap-3 px-4 py-3.5
                cursor-pointer hover:bg-bg-hover active:scale-[0.998]
                transition-all select-none items-center
                ${!isExpanded && gi < eventGroups.length - 1 ? 'border-b border-border-subtle/50' : ''}`}
            >
              <ChevronRight
                size={14}
                className={`text-text-muted transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
              />
              <span className="text-text-secondary text-xs tabular-nums font-mono">
                {group.date}
              </span>
              <span className="font-semibold text-text-primary text-sm truncate">{group.trackName}</span>
              <span className="text-text-muted text-sm tabular-nums text-right inline-flex items-center justify-end gap-1.5">
                <span>{sessionCount}</span>
                {isGroupProcessed ? (
                  <span title={t('lapAnalysis.statusCompleted')} className="inline-flex items-center text-emerald-400">
                    <CheckCircle2 size={13} />
                  </span>
                ) : (
                  <span title={t('lapAnalysis.statusPending')} className="inline-flex items-center text-amber-400">
                    <Clock size={13} />
                  </span>
                )}
              </span>
              <span className="text-right text-text-secondary text-xs self-center">
                {groupWeather && groupWeather.condition != null ? (
                  <WeatherBadge condition={String(groupWeather.condition)} temperatureC={groupWeather.temperatureC ?? 0} />
                ) : null}
              </span>
              <span className={`text-right font-mono font-semibold text-sm ${isGroupTrackBest ? 'text-purple' : 'text-green'}`}>
                {isFinite(groupBestMs) ? formatTime(groupBestMs) : '—'}
              </span>
            </div>

            {/* Expanded: session sub-header + session rows (desktop) */}
            {isExpanded && (
              <>
                {/* Session column header */}
                <div className={`hidden sm:grid ${SESSION_GRID} gap-3 px-4 py-2 bg-bg-surface/80
                  text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted border-t border-border-subtle`}
                >
                  <span></span>
                  <span>{t('sessionList.time')}</span>
                  <span>{t('sessionList.driver')}</span>
                  <span className="text-right">{t('sessionList.laps')}</span>
                  <span className="text-right">{t('sessionList.weather')}</span>
                  <span className="text-right">{t('sessionList.best')}</span>
                </div>

                {/* Session rows */}
                {group.sessions.map((s, si) => {
                  const isTrackBest = s.bestLapTimeMs > 0 && trackBestMap[s.trackName] === s.bestLapTimeMs;
                  const isSessionProcessed = s.analysisStatus === 'completed';
                  return (
                    <div
                      key={s.id}
                      onClick={() => navigate(`#/session/${s.id}`)}
                      className={`
                        hidden sm:grid ${SESSION_GRID} gap-3
                        px-4 py-3 cursor-pointer
                        hover:bg-bg-hover active:scale-[0.995]
                        transition-all group bg-bg-surface/80
                        ${si < group.sessions.length - 1 ? 'border-b border-border-subtle' : ''}
                        ${si === group.sessions.length - 1 && gi < eventGroups.length - 1 ? 'border-b border-border-subtle/50' : ''}
                      `}
                    >
                      <span></span>
                      <span className="text-text-muted text-xs tabular-nums font-mono self-center">
                        {s.time || '—'}
                      </span>
                      <span className="text-text-secondary text-sm truncate self-center flex items-center gap-1.5">
                        {s.driverName}
                        {s.ownerId && s.ownerId !== user?.id && s.ownerName && (
                          <span className="inline-flex items-center gap-1 text-[0.65rem] font-medium px-1.5 py-0.5 rounded-sm bg-accent/10 text-accent" title={t('sharing.owner', { name: s.ownerName })}>
                            <Share2 size={10} />
                            {s.ownerName}
                          </span>
                        )}
                      </span>
                      <span className="text-text-muted text-sm tabular-nums text-right self-center inline-flex items-center justify-end gap-1.5">
                        <span>{s.completedLaps}</span>
                        {isSessionProcessed ? (
                          <span title={t('lapAnalysis.statusCompleted')} className="inline-flex items-center text-emerald-400">
                            <CheckCircle2 size={13} />
                          </span>
                        ) : (
                          <span title={t('lapAnalysis.statusPending')} className="inline-flex items-center text-amber-400">
                            <Clock size={13} />
                          </span>
                        )}
                      </span>
                      <span className="text-right text-text-secondary text-xs self-center">
                        {s.weather && s.weather.condition != null ? (
                          <WeatherBadge condition={String(s.weather.condition)} temperatureC={s.weather.temperatureC ?? 0} />
                        ) : null}
                      </span>
                      <span className={`text-right font-mono font-semibold text-sm self-center flex items-center justify-end gap-1.5 ${isTrackBest ? 'text-purple' : 'text-green'}`}>
                        {s.freshTyres && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald/10 text-emerald text-[0.6rem] font-semibold leading-none" title={t('sessionMeta.fresh')}>
                            🌿
                          </span>
                        )}
                        {formatTime(s.bestLapTimeMs)}
                      </span>
                    </div>
                  );
                })}
              </>
            )}

            {/* Event parent row (mobile) */}
            <div
              onClick={() => toggleEvent(group.key)}
              className={`sm:hidden px-4 py-3.5 cursor-pointer hover:bg-bg-hover
                transition-colors active:scale-[0.998] select-none
                ${!isExpanded && gi < eventGroups.length - 1 ? 'border-b border-border-subtle/50' : ''}`}
            >
              <div className="flex items-center gap-2.5">
                <ChevronRight
                  size={14}
                  className={`text-text-muted transition-transform duration-200 shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <span className="text-sm font-semibold text-text-primary truncate flex items-center gap-1.5">
                        <span className="truncate">{group.trackName}</span>
                        <span className="text-text-muted font-normal shrink-0">({sessionCount})</span>
                        {isGroupProcessed ? (
                          <span title={t('lapAnalysis.statusCompleted')} className="inline-flex items-center text-emerald-400 shrink-0">
                            <CheckCircle2 size={12} />
                          </span>
                        ) : (
                          <span title={t('lapAnalysis.statusPending')} className="inline-flex items-center text-amber-400 shrink-0">
                            <Clock size={12} />
                          </span>
                        )}
                      </span>
                      <span className="text-[0.7rem] text-text-muted font-mono">{group.date}</span>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {groupWeather && groupWeather.condition != null && (
                        <WeatherBadge condition={String(groupWeather.condition)} temperatureC={groupWeather.temperatureC ?? 0} />
                      )}
                      {isFinite(groupBestMs) && (
                        <span className={`text-[0.7rem] font-semibold font-mono ${isGroupTrackBest ? 'text-purple' : 'text-green'}`}>
                          {formatTime(groupBestMs)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Expanded child session rows (mobile) */}
            {isExpanded && group.sessions.map((s, si) => {
              const isTrackBest = s.bestLapTimeMs > 0 && trackBestMap[s.trackName] === s.bestLapTimeMs;
              const isSessionProcessed = s.analysisStatus === 'completed';
              return (
                <button
                  key={s.id}
                  onClick={() => navigate(`#/session/${s.id}`)}
                  className={`sm:hidden w-full text-left pl-10 pr-4 py-3 hover:bg-bg-hover
                    transition-colors group border-t border-border-subtle bg-bg-surface/80
                    ${si === group.sessions.length - 1 && gi < eventGroups.length - 1 ? 'border-b border-border-subtle/50' : ''}`}
                >
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex flex-col gap-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[0.7rem] text-text-muted font-mono shrink-0">{s.time || '—'}</span>
                          <span className="text-[0.7rem] text-text-secondary truncate flex items-center gap-1.5">
                            {s.driverName}
                            {s.ownerId && s.ownerId !== user?.id && s.ownerName && (
                              <span className="inline-flex items-center gap-1 text-[0.6rem] font-medium px-1 py-0.5 rounded-sm bg-accent/10 text-accent">
                                <Share2 size={9} />
                                {s.ownerName}
                              </span>
                            )}
                          </span>
                        </div>
                        <span className="text-[0.7rem] text-text-muted inline-flex items-center gap-1.5">
                          <span>{t('sessionList.lapsUnit', { n: s.completedLaps })}</span>
                          {isSessionProcessed ? (
                            <span title={t('lapAnalysis.statusCompleted')} className="inline-flex items-center text-emerald-400 shrink-0">
                              <CheckCircle2 size={12} />
                            </span>
                          ) : (
                            <span title={t('lapAnalysis.statusPending')} className="inline-flex items-center text-amber-400 shrink-0">
                              <Clock size={12} />
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {s.weather && s.weather.condition != null && (
                          <WeatherBadge condition={String(s.weather.condition)} temperatureC={s.weather.temperatureC ?? 0} />
                        )}
                        <span className={`text-sm font-bold font-mono flex items-center gap-1 ${isTrackBest ? 'text-purple' : 'text-green'}`}>
                          {s.freshTyres && (
                            <span className="inline-flex items-center px-1 py-0.5 rounded-full bg-emerald/10 text-emerald text-[0.55rem] font-semibold leading-none">
                              🌿
                            </span>
                          )}
                          {formatTime(s.bestLapTimeMs)}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
